/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { type Registry as Services, service } from '@ember/service';
import { action } from '@ember/object';
import { runTask } from 'ember-lifeline';
import UserModel from 'uplisting-frontend/models/user';
import PriceModel from 'uplisting-frontend/models/price';
import BookingModel from 'uplisting-frontend/models/booking';
import ExtraPriceModel from 'uplisting-frontend/models/extra-price';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/base';

interface IArgs {
  booking: BookingModel;
}

export interface BookingPriceEditSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export default class UiBookingPriceEditComponent extends Component<BookingPriceEditSignature> {
  @service store!: Services['store'];
  @service router!: Services['router'];
  @service session!: Services['session'];
  @service notifications!: Services['notifications'];

  @service('repositories/price')
  priceRepository!: Services['repositories/price'];

  @cached @tracked showModal = true;

  @cached @tracked isSavingPrices = false;
  @cached @tracked isDiscountChanged = false;
  @cached @tracked isChargeChanged = false;

  @cached
  get booking(): BookingModel {
    return this.args.booking;
  }

  @cached
  get currentUser(): UserModel {
    return this.session.currentUser as UserModel;
  }

  @cached
  get changeset(): GenericChangeset<PriceModel> {
    return this.priceRepository.buildChangeset(this.booking.price);
  }

  @cached
  get cancelPriceEditRoute(): string {
    switch (this.router.currentRouteName) {
      case 'action.bookings.price.edit':
        return 'action.bookings.price';
      default:
        return '';
    }
  }

  @action
  handleChangeDiscount(discount: ExtraPriceModel, value: number): void {
    this.isDiscountChanged = true;

    discount.amount = value;
  }

  @action
  handleChangeCharge(charge: ExtraPriceModel, value: number): void {
    this.isChargeChanged = true;

    charge.amount = value;
  }

  @action
  handleCloseModal(): void {
    if (this.isDiscountChanged) {
      this.booking.price.discounts.forEach((d) => d.rollbackAttributes());
    }

    if (this.isChargeChanged) {
      this.booking.price.charges.forEach((c) => c.rollbackAttributes());
    }

    this.changeset.rollback();

    this.showModal = false;

    // waiting for the modal animation to complete(modal closing animation takes 0.3s to complete)
    runTask(
      this,
      async () => {
        await this.router.transitionTo(this.cancelPriceEditRoute);
      },
      300,
    );
  }

  @action
  async handleFormSubmit(): Promise<void> {
    const { changeset } = this;

    const oldBalance = changeset.hostEarnings as number;

    this.isSavingPrices = true;
    changeset.hostEdited = true;

    try {
      const price = await changeset.save();

      this.notifications.info(
        'action_bookings_price_edit.modal.notifications.applied',
      );

      this.updateBookingPrice(oldBalance, price.hostEarnings as number);

      this.handleCloseModal();
    } catch {
      this.notifications.error();
    } finally {
      this.isSavingPrices = false;
      this.isDiscountChanged = false;
      this.isChargeChanged = false;
    }
  }

  private updateBookingPrice(oldBalance: number, newBalance: number) {
    const diff = newBalance - oldBalance;

    if (diff === 0) {
      return;
    }

    const { store, currentUser } = this;

    const record = store.createRecord('history-booking-price', {
      amount: diff,
      createdAt: new Date(),
      userEmail: currentUser.email,
      userName: currentUser.name,
    });

    this.booking.priceChanges.push(record);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::Price::Edit': typeof UiBookingPriceEditComponent;
  }
}
